import { Lock, Info } from "lucide-react";
import { Panel } from "@/components/admin/ui";
import { inputClass, saveBtnClass } from "./orgFormStyles";

export default function OrgAccessLimitsCard({ limits, limitsForm, setLimitsForm, usage, limitsStatus, onSave }) {
  const row = (key, label, usageText) => (
    <tr className="border-b border-slate-100 last:border-0 dark:border-white/5" data-testid={`org-limit-row-${key}`}>
      <td className="py-3 pr-4 text-sm font-semibold text-slate-700 dark:text-slate-300">{label}</td>
      <td className="py-3 pr-4 text-sm text-slate-500 dark:text-slate-400" data-testid={`org-limit-usage-${key}`}>{usageText}</td>
      <td className="py-3">
        <input
          type="number"
          min={key === "max_users_per_team" ? 1 : 0}
          value={limitsForm[key]}
          onChange={(e) => setLimitsForm((f) => ({ ...f, [key]: e.target.value === "" ? "" : Number(e.target.value) }))}
          data-testid={`org-limit-input-${key}`}
          className={`${inputClass} mt-0 w-24 text-center`}
        />
      </td>
    </tr>
  );

  return (
    <Panel
      title="Platform Access Limits"
      subtitle="Platform-controlled entitlements. Organization users can never exceed these limits."
      testId="org-limits-card"
      action={
        <button type="button" onClick={onSave} disabled={limitsStatus.saving} data-testid="org-limits-save-btn" className={saveBtnClass}>
          {limitsStatus.saving ? "Saving…" : "Save Limits"}
        </button>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-left" data-testid="org-limits-panel">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.1em] text-slate-400 dark:border-white/10 dark:text-slate-500">
              <th className="py-2.5 pr-4 font-bold">Access Type</th>
              <th className="py-2.5 pr-4 font-bold">Current Usage</th>
              <th className="py-2.5 font-bold">Platform Limit</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100 last:border-0 dark:border-white/5" data-testid="org-limit-row-max_org_owner">
              <td className="py-3 pr-4">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <Lock size={13} className="text-slate-400" /> Organization Owner
                </span>
              </td>
              <td className="py-3 pr-4 text-sm text-slate-500 dark:text-slate-400">1 / 1</td>
              <td className="py-3">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400">Fixed</span>
              </td>
            </tr>
            {row("max_org_admins", "Organization Admins", `${usage?.org_admins ?? 0} / ${limits?.max_org_admins ?? "—"}`)}
            {row("max_teams", "Teams", `${usage?.teams ?? 0} / ${limits?.max_teams ?? "—"}`)}
            {row("max_users_per_team", "Users per Team", "—")}
            {row("max_nova_billing_users", "Nova Billing Users", `${usage?.nova_billing_users ?? 0} / ${limits?.max_nova_billing_users ?? "—"}`)}
          </tbody>
        </table>
      </div>
      {limitsStatus.error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-limits-error">{limitsStatus.error}</p>}
      {limitsStatus.savedFlash && <p className="mt-3 text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="org-limits-saved-flash">Saved</p>}
      <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300" data-testid="org-limits-note">
        <Info size={14} className="mt-0.5 shrink-0" />
        <p>Lowering a limit below current usage will not remove existing users, teams, or access — the organization keeps what it already has (grandfathered) but cannot add more in that category until usage is brought back within the limit, or the limit is raised again.</p>
      </div>
    </Panel>
  );
}
